import { useState, useEffect } from 'react';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area
} from 'recharts';
import { FaChartLine, FaChartBar, FaCalendarAlt, FaDownload } from 'react-icons/fa';
import { useAuth } from './AuthContext';
import './Charts.css';

function Charts({ addToast }) {
  const { token } = useAuth();
  const [inquiryData, setInquiryData] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [categoryData, setCategoryData] = useState([]);
  const [statusData, setStatusData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('6months');
  const [activeChart, setActiveChart] = useState('bar');

  // API endpoints
  const endpoints = {
    popInquiry: 'https://airexheater.in/api/popInquiry',
    inquiry: 'https://airexheater.in/api/inquiry',
    contactForm: 'https://airexheater.in/api/contact',
    bulkInquiry: 'https://airexheater.in/api/bulk'
  };

  useEffect(() => {
    fetchAllData();
  }, [timeRange]);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      // Add authorization header with token
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };

      const [popRes, inquiryRes, contactRes, bulkRes] = await Promise.all([
        fetch(endpoints.popInquiry, { headers }),
        fetch(endpoints.inquiry, { headers }),
        fetch(endpoints.contactForm, { headers }),
        fetch(endpoints.bulkInquiry, { headers })
      ]);

      // Check if responses are ok
      if (!popRes.ok || !inquiryRes.ok || !contactRes.ok || !bulkRes.ok) {
        throw new Error('Failed to fetch data from server');
      }

      const popData = await popRes.json();
      const inquiryData = await inquiryRes.json();
      const contactData = await contactRes.json();
      const bulkData = await bulkRes.json();

      const allInquiries = [
        ...(Array.isArray(popData) ? popData : []),
        ...(Array.isArray(inquiryData) ? inquiryData : []),
        ...(Array.isArray(contactData) ? contactData : []),
        ...(Array.isArray(bulkData) ? bulkData : [])
      ];

      processChartData(allInquiries);
      if (addToast) {
        addToast('Dashboard data loaded successfully', 'success', 2000);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      if (addToast) {
        addToast('Failed to load dashboard data', 'error', 3000);
      }
      // Use sample data for demo
      generateAndProcessSampleData();
    } finally {
      setLoading(false);
    }
  };

  const generateAndProcessSampleData = () => {
    const sampleInquiries = [];
    const types = ['POP Inquiry', 'General Inquiry', 'Contact Form', 'Bulk Inquiry'];
    const statuses = ['pending', 'approved', 'rejected'];
    
    for (let i = 0; i < 200; i++) {
      const date = new Date();
      date.setDate(date.getDate() - Math.floor(Math.random() * 180));
      sampleInquiries.push({
        _id: `sample_${i}`,
        type: types[Math.floor(Math.random() * types.length)],
        status: statuses[Math.floor(Math.random() * statuses.length)],
        createdAt: date.toISOString()
      });
    }
    processChartData(sampleInquiries);
  };

  const processChartData = (inquiries) => {
    // Process monthly data
    const monthlyStats = {};
    const categoryStats = {};
    const statusStats = {
      'POP Inquiry': { pending: 0, approved: 0, rejected: 0 },
      'General Inquiry': { pending: 0, approved: 0, rejected: 0 },
      'Contact Form': { pending: 0, approved: 0, rejected: 0 },
      'Bulk Inquiry': { pending: 0, approved: 0, rejected: 0 }
    };

    inquiries.forEach(inquiry => {
      // Determine inquiry type
      let type = '';
      if (inquiry.firstName) type = 'POP Inquiry';
      else if (inquiry.userName) type = 'General Inquiry';
      else if (inquiry.name && !inquiry.fullName) type = 'Contact Form';
      else if (inquiry.fullName) type = 'Bulk Inquiry';
      else type = 'Other';

      // Monthly data
      const date = new Date(inquiry.createdAt);
      const monthYear = date.toLocaleString('default', { month: 'short', year: 'numeric' });
      
      if (!monthlyStats[monthYear]) {
        monthlyStats[monthYear] = {
          month: monthYear,
          'POP Inquiry': 0,
          'General Inquiry': 0,
          'Contact Form': 0,
          'Bulk Inquiry': 0,
          total: 0
        };
      }
      monthlyStats[monthYear][type]++;
      monthlyStats[monthYear].total++;

      // Category stats
      if (!categoryStats[type]) {
        categoryStats[type] = 0;
      }
      categoryStats[type]++;

      // Status stats
      const status = inquiry.status || 'pending';
      if (statusStats[type] && statusStats[type][status] !== undefined) {
        statusStats[type][status]++;
      }
    });

    // Convert to array and sort by date
    const monthlyArray = Object.values(monthlyStats);
    monthlyArray.sort((a, b) => {
      const dateA = new Date(a.month);
      const dateB = new Date(b.month);
      return dateA - dateB;
    });

    // Apply time range filter
    let filteredMonthly = monthlyArray;
    if (timeRange === '6months') {
      filteredMonthly = monthlyArray.slice(-6);
    } else if (timeRange === '1year') {
      filteredMonthly = monthlyArray.slice(-12);
    }

    setMonthlyData(filteredMonthly);

    // Category data for pie chart
    const categoryArray = Object.entries(categoryStats).map(([name, value]) => ({
      name,
      value
    }));
    setCategoryData(categoryArray);

    // Status data for radial bar
    const statusArray = Object.entries(statusStats).map(([name, stats]) => ({
      name,
      pending: stats.pending,
      approved: stats.approved,
      rejected: stats.rejected,
      total: stats.pending + stats.approved + stats.rejected
    }));
    setStatusData(statusArray);

    // Inquiry data for line chart
    const inquiryArray = filteredMonthly.map(month => ({
      month: month.month,
      'POP Inquiry': month['POP Inquiry'],
      'General Inquiry': month['General Inquiry'],
      'Contact Form': month['Contact Form'],
      'Bulk Inquiry': month['Bulk Inquiry'],
      total: month.total
    }));
    setInquiryData(inquiryArray);
  };

  const COLORS = ['#00adef', '#27ae60', '#e67e22', '#9b59b6', '#3498db', '#e74c3c'];
  const STATUS_COLORS = {
    pending: '#ff9800',
    approved: '#4caf50',
    rejected: '#f44336'
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="tooltip-label">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const downloadChartData = () => {
    const headers = ['Month', 'POP Inquiry', 'General Inquiry', 'Contact Form', 'Bulk Inquiry', 'Total'];
    const csvData = inquiryData.map(item => [
      item.month,
      item['POP Inquiry'],
      item['General Inquiry'],
      item['Contact Form'],
      item['Bulk Inquiry'],
      item.total
    ]);
    
    const csvContent = [headers, ...csvData].map(row => row.join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `dashboard_analytics_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    if (addToast) {
      addToast('Analytics data exported successfully', 'success', 3000);
    }
  };

  if (loading) {
    return (
      <div className="charts-container">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading analytics data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="charts-container">
      <div className="charts-header">
        <h2>
          <FaChartLine /> Analytics Dashboard
        </h2>
        <div className="charts-controls">
          <div className="time-range-selector">
            <FaCalendarAlt />
            <select value={timeRange} onChange={(e) => setTimeRange(e.target.value)}>
              <option value="6months">Last 6 Months</option>
              <option value="1year">Last 12 Months</option>
              <option value="all">All Time</option>
            </select>
          </div>
          <div className="chart-type-selector">
            <button 
              className={activeChart === 'bar' ? 'active' : ''} 
              onClick={() => setActiveChart('bar')}
              title="Bar Chart"
            >
              <FaChartBar />
            </button>
            <button 
              className={activeChart === 'line' ? 'active' : ''} 
              onClick={() => setActiveChart('line')}
              title="Line Chart"
            >
              <FaChartLine />
            </button>
            <button 
              className={activeChart === 'area' ? 'active' : ''} 
              onClick={() => setActiveChart('area')}
              title="Area Chart"
            >
              📊
            </button>
          </div>
          <button onClick={downloadChartData} className="download-btn">
            <FaDownload /> Export Data
          </button>
        </div>
      </div>

      {/* Stats Summary Cards */}
      <div className="stats-summary">
        <div className="summary-card">
          <div className="summary-icon">📊</div>
          <div className="summary-info">
            <h3>Total Inquiries</h3>
            <p>{inquiryData.reduce((sum, item) => sum + item.total, 0)}</p>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon">📈</div>
          <div className="summary-info">
            <h3>Monthly Average</h3>
            <p>{Math.round(inquiryData.reduce((sum, item) => sum + item.total, 0) / (inquiryData.length || 1))}</p>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon">🔥</div>
          <div className="summary-info">
            <h3>Most Active</h3>
            <p>
              {categoryData.length > 0 ? 
                categoryData.reduce((max, item) => item.value > max.value ? item : max, categoryData[0])?.name : 
                'N/A'}
            </p>
          </div>
        </div>
        <div className="summary-card">
          <div className="summary-icon">📅</div>
          <div className="summary-info">
            <h3>Peak Month</h3>
            <p>
              {inquiryData.length > 0 ?
                inquiryData.reduce((max, item) => item.total > max.total ? item : max, inquiryData[0])?.month :
                'N/A'}
            </p>
          </div>
        </div>
      </div>

      {/* Main Charts Grid */}
      <div className="charts-grid">
        {/* Main Chart - Bar/Line/Area */}
        <div className="chart-card main-chart">
          <h3>Inquiry Trends Over Time</h3>
          <ResponsiveContainer width="100%" height={400}>
            {activeChart === 'bar' && (
              <BarChart data={inquiryData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="POP Inquiry" fill="#00adef" />
                <Bar dataKey="General Inquiry" fill="#27ae60" />
                <Bar dataKey="Contact Form" fill="#e67e22" />
                <Bar dataKey="Bulk Inquiry" fill="#9b59b6" />
              </BarChart>
            )}
            {activeChart === 'line' && (
              <LineChart data={inquiryData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line type="monotone" dataKey="POP Inquiry" stroke="#00adef" strokeWidth={2} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="General Inquiry" stroke="#27ae60" strokeWidth={2} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="Contact Form" stroke="#e67e22" strokeWidth={2} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="Bulk Inquiry" stroke="#9b59b6" strokeWidth={2} dot={{ r: 4 }} />
              </LineChart>
            )}
            {activeChart === 'area' && (
              <AreaChart data={inquiryData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Area type="monotone" dataKey="POP Inquiry" stackId="1" stroke="#00adef" fill="#00adef" fillOpacity={0.6} />
                <Area type="monotone" dataKey="General Inquiry" stackId="1" stroke="#27ae60" fill="#27ae60" fillOpacity={0.6} />
                <Area type="monotone" dataKey="Contact Form" stackId="1" stroke="#e67e22" fill="#e67e22" fillOpacity={0.6} />
                <Area type="monotone" dataKey="Bulk Inquiry" stackId="1" stroke="#9b59b6" fill="#9b59b6" fillOpacity={0.6} />
              </AreaChart>
            )}
          </ResponsiveContainer>
        </div>

        {/* Pie Chart - Distribution */}
        <div className="chart-card">
          <h3>Inquiry Distribution</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={categoryData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {categoryData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Status Breakdown */}
        <div className="chart-card">
          <h3>Inquiry Status by Type</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={statusData} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis type="number" />
              <YAxis dataKey="name" type="category" width={100} />
              <Tooltip />
              <Legend />
              <Bar dataKey="pending" stackId="a" fill={STATUS_COLORS.pending} name="Pending" />
              <Bar dataKey="approved" stackId="a" fill={STATUS_COLORS.approved} name="Approved" />
              <Bar dataKey="rejected" stackId="a" fill={STATUS_COLORS.rejected} name="Rejected" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Total Inquiries Trend */}
      <div className="chart-card full-width">
        <h3>Total Inquiries Trend</h3>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={inquiryData}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="month" />
            <YAxis />
            <Tooltip />
            <Legend />
            <Area type="monotone" dataKey="total" stroke="#00adef" fill="#00adef" fillOpacity={0.3} strokeWidth={3} />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

export default Charts;